import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

type StoredGuestToken = {
  access_token: string;
  expiresAt: number;
};

@Injectable({
  providedIn: 'root',
})
export class GuestTokenService {
  private readonly storageKey = 'guest_access_token';
  private inFlight$?: Observable<string>;

  constructor(private http: HttpClient) { }

  ensureGuestToken(): Observable<string> {
    // const cached = this.getValidCachedToken();
    // if (cached) {
    //   return of(cached);
    // }
    if (!this.inFlight$) {
      this.inFlight$ = this.requestNewToken().pipe(
        shareReplay(1),
        finalize(() => {
          this.inFlight$ = undefined;
        })
      );
    }

    return this.inFlight$;
  }

  private getValidCachedToken(): string | null {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as StoredGuestToken;
      const bufferMs = 60_000; // Refresh 1 minute before expiry
      if (parsed?.access_token && parsed?.expiresAt && parsed.expiresAt - bufferMs > Date.now()) {
        return parsed.access_token;
      }
    } catch {
      // Ignore parsing errors
    }
    return null;
  }

  private requestNewToken(): Observable<string> {
    const params = new HttpParams()
      .set('grant_type', 'client_credentials')
      .set('scope', environment.guestTokenScope || 'openid address email profile');

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http
      .post<any>(environment.guestTokenUrl, params.toString(), { headers })
      .pipe(
        map((res) => {
          //console.log('Guest token response', res);
          const token = res?.access_token || '';
          const expires = res?.expires_in ? Number(res.expires_in) : 0;
          this.persistToken(token, expires);
          return token;
        }),
        catchError((error) => {
          //console.error('Failed to fetch guest token', error);
          return throwError(() => error);
        })
      );
  }

  private persistToken(token: string, expiresInSeconds: number): void {
    if (!token) {
      return;
    }
    const expiresAt = Date.now() + Math.max(expiresInSeconds, 0) * 1000;
    try {
      localStorage.setItem(
        this.storageKey,
        JSON.stringify({ access_token: token, expiresAt })
      );
    } catch {
      // Ignore storage errors
    }
  }
}
